import { create } from 'zustand';
import {
  apiClient,
  AUTH_EXPIRED_BROADCAST_KEY,
  isActivePracticeRoute,
  type AuthExpiredEventDetail,
} from '@/features/shared/api/client';

const STORAGE_KEY = 'pps-student-auth';

export interface StudentAuthUser {
  id: string;
  displayName: string;
  phoneMasked?: string | null;
  contactEmail?: string | null;
  avatarUrl?: string | null;
  emailAvatarUrl?: string | null;
  registrationCompleted: boolean;
  preferredPracticeMode?: 'practice' | 'mock' | null;
}

interface StudentAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresInSeconds: number;
  user: StudentAuthUser | null;
  setSession: (payload: {
    accessToken: string;
    refreshToken: string;
    expiresInSeconds: number;
    user: StudentAuthUser;
  }) => void;
  clearSession: () => void;
  updateUser: (payload: Partial<StudentAuthUser>) => void;
}

type StoredAuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  expiresInSeconds: number;
  user: StudentAuthUser | null;
};

function emptyStoredState(): StoredAuthState {
  return {
    accessToken: null,
    refreshToken: null,
    expiresInSeconds: 0,
    user: null,
  };
}

function parseStoredState(raw: string | null): StoredAuthState {
  if (!raw) {
    return emptyStoredState();
  }
  try {
    const parsed = JSON.parse(raw) as {
      accessToken: string | null;
      refreshToken: string | null;
      expiresInSeconds: number;
      user: StudentAuthUser | null;
    };
    return {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
      expiresInSeconds: Number(parsed.expiresInSeconds || 0),
      user: parsed.user
        ? {
            ...parsed.user,
            registrationCompleted: parsed.user.registrationCompleted ?? true,
          }
        : null,
    };
  } catch {
    return emptyStoredState();
  }
}

function readStoredState() {
  if (typeof window === 'undefined') {
    return emptyStoredState();
  }
  const parsed = parseStoredState(window.localStorage.getItem(STORAGE_KEY));
  apiClient.setAuthSession(parsed.accessToken, parsed.refreshToken);
  return parsed;
}

const initialState = readStoredState();

export const useStudentAuthStore = create<StudentAuthState>((set) => ({
  accessToken: initialState.accessToken,
  refreshToken: initialState.refreshToken,
  expiresInSeconds: initialState.expiresInSeconds,
  user: initialState.user,
  setSession: (payload) => {
    apiClient.setAuthSession(payload.accessToken, payload.refreshToken);
    set(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      }
      return {
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        expiresInSeconds: payload.expiresInSeconds,
        user: payload.user,
      };
    });
  },
  clearSession: () => {
    apiClient.setAuthSession(null, null);
    set(() => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY);
      }
      return {
        accessToken: null,
        refreshToken: null,
        expiresInSeconds: 0,
        user: null,
      };
    });
  },
  updateUser: (payload) =>
    set((state) => {
      if (!state.user) {
        return state;
      }
      const nextUser = { ...state.user, ...payload };
      if (typeof window !== 'undefined') {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
            expiresInSeconds: state.expiresInSeconds,
            user: nextUser,
          }),
        );
      }
      return { ...state, user: nextUser };
    }),
}));

apiClient.configureAuthLifecycle({
  onSessionRefresh: (payload) => {
    useStudentAuthStore.getState().setSession({
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      expiresInSeconds: payload.expiresInSeconds,
      user: payload.user as StudentAuthUser,
    });
  },
  onSessionExpired: (_detail: AuthExpiredEventDetail) => {
    // Active practice pages keep their session state so AuthExpiredDialog can
    // pause the task and let the student explicitly re-authenticate.
    const activePracticeRoute = typeof window !== 'undefined'
      && isActivePracticeRoute(window.location.pathname);
    if (!activePracticeRoute) {
      useStudentAuthStore.getState().clearSession();
    }
  },
});

declare global {
  interface Window {
    __ppsStudentAuthStorageSyncBound?: boolean;
  }
}

if (typeof window !== 'undefined' && !window.__ppsStudentAuthStorageSyncBound) {
  window.__ppsStudentAuthStorageSyncBound = true;
  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_EXPIRED_BROADCAST_KEY && event.newValue) {
      if (!isActivePracticeRoute(window.location.pathname)) {
        useStudentAuthStore.getState().clearSession();
      }
      return;
    }
    if (event.key !== STORAGE_KEY) {
      return;
    }
    const parsed = parseStoredState(event.newValue);
    apiClient.setAuthSession(parsed.accessToken, parsed.refreshToken);
    useStudentAuthStore.setState({
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresInSeconds: parsed.expiresInSeconds,
      user: parsed.user,
    });
  });
}
